<template>
  <BaseModal
    :show="show"
    @close="close"
  >
    <div class="modal-body mb-4">
      <div
        v-show="currentPane === 'fileUpload'"
        class="flex flex-col items-center"
      >
        <header class="text-center pb-4">
          <h3
            class="my-0 font-bold"
            v-text="'Select Document'"
          />
        </header>
        <div class="text-center w-full">
          <BaseDropzone
            ref="documentUpload"
            :options="dropzoneOptions"
            @dropzone-file-added="filesHaveBeenAdded"
            @dropzone-queue-complete="uploadHasCompleted"
            @dropzone-success="fileHasBeenUploaded"
            @dropzone-removed-file="fileHasBeenRemoved"
          />
        </div>
        <div
          v-if="isUploading || uploadingIsDone"
          class="mt-6"
        >
          <button
            type="button"
            class="btn"
            :class="{ 'load': isUploading }"
            :disabled="!uploadingIsDone"
            :aria-disabled="!uploadingIsDone"
            @click.prevent="setUploadDestination('existingFolder')"
            v-html="confirmUploadButtonText"
          />
        </div>
      </div>
      <div v-show="currentPane === 'existingFolder'">
        <div class="flex flex-col items-center z-100">
          <header class="text-center pb-4">
            <h3
              class="my-0 font-bold"
              v-text="'Add to Folder'"
            />
          </header>
          <div class="my-12 w-full">
            <Multiselect
              id="upload-document-target-folder"
              v-model="targetFolder"
              :options="existingFolders"
              label="title"
              track-by="uuid"
              placeholder="Select folder"
              open-direction="bottom"
              :allow-empty="false"
            >
              <template
                slot="option"
                slot-scope="props"
              >
                <div class="option__desc">
                  <span
                    class="option__title"
                    :class="calculateClass(props.option.level)"
                    v-text="props.option.title"
                  />
                </div>
              </template>
            </Multiselect>
          </div>
          <div class="mb-6">
            <button
              type="button"
              class="btn"
              @click.prevent="save"
              v-text="'Save'"
            />
            <a
              href="#"
              class="mx-4"
              @click.prevent="close"
              v-text="'Cancel'"
            />
          </div>
          <div class="mb-6 mt-4">
            <a
              href="#"
              class="text-sm no-underline"
              @click.prevent="setUploadDestination('newFolder')"
            >
              or, create a new folder &rarr;
            </a>
          </div>
        </div>
      </div>
      <div v-show="currentPane === 'newFolder'">
        <div class="flex flex-col items-center z-100">
          <header class="text-center pb-4">
            <h3
              class="my-0 font-bold"
              v-text="'Create a New Folder'"
            />
          </header>
          <Multiselect
            id="upload-document-parent-folder"
            v-model="newFolder.parentFolder"
            :options="existingFolders"
            label="title"
            track-by="uuid"
            placeholder="Select a parent folder"
            open-direction="bottom"
          >
            <template
              slot="option"
              slot-scope="props"
            >
              <div class="option__desc">
                <span
                  class="option__title"
                  :class="calculateClass(props.option.level)"
                  v-text="props.option.title"
                />
              </div>
            </template>
          </Multiselect>
          <input
            v-model="newFolder.title"
            type="text"
            class="input mt-4"
            placeholder="New folder title"
            autofocus
          >
          <div class="mb-6">
            <button
              type="button"
              class="btn"
              :disabled="!newFolderHasATitle"
              @click.prevent="save"
              v-text="'Save'"
            />
            <a
              href="#"
              class="mx-4"
              @click.prevent="close"
              v-text="'Cancel'"
            />
          </div>
          <div class="mb-6">
            <a
              href="#"
              class="text-sm no-underline"
              @click.prevent="setUploadDestination('existingFolder')"
            >
              &larr; back to existing folders
            </a>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script type="text/babel">
import Multiselect from 'vue-multiselect'

export default {
  name: 'UploadDocumentModal',
  components: {
    Multiselect,
  },
  props: {
    folderUuid: {
      type: String,
      required: false,
      default: '',
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      currentPane: 'fileUpload',
      isUploading: false,
      queuedCount: 0,
      uploadingTo: 'existingFolder',
      uploadedFileUuids: [],
      targetFolder: null,
      newFolder: {
        title: '',
        parentFolder: {
          uuid: '',
        },
      },
      dropzoneOptions: {
        url: this.$urlGenerator.active('api/v1/documents/upload'),
      },
    }
  },
  computed: {
    confirmUploadButtonText() {
      if (this.isUploading) {
        return 'Uploading'
      }

      if (this.uploadingIsDone) {
        return 'Next'
      }

      return 'Confirm'
    },
    uploadingIsDone() {
      return this.uploadedFileUuids.length && !this.isUploading
    },
    existingFolders() {
      return this.$store.getters.flatFolderTree
    },
    newFolderHasATitle() {
      return this.newFolder.title
    },
  },
  watch: {
    show() {
      if (this.show) {
        if (!this.$store.state.document.folders.data.length) {
          this.$store.dispatch('documentGetFolderTree')
        } else {
          this.setDefaultFolders()
        }
      }
    },
    existingFolders(end, start) {
      if (this.folderUuid && end.length) {
        this.setDefaultFolders()
      }
    },
  },
  methods: {
    save() {
      this.isUploading = true

      switch (this.uploadingTo) {
        case 'existingFolder':
          this.saveToExistingFolder(this.targetFolder.uuid).then(() => {
            this.isUploading = false

            window.location = this.$urlGenerator.active(
              `documents/folders/${this.targetFolder.uuid}`
            )
          })

          break
        case 'newFolder':
          this.saveToNewFolder().then((response) => {
            this.saveToExistingFolder(response.data.uuid).then(() => {
              this.isUploading = false

              window.location = this.$urlGenerator.active(
                `documents/folders/${response.data.uuid}`
              )
            })
          })

          break
      }
    },
    saveToNewFolder() {
      return new Promise((resolve, reject) => {
        this.$api
          .active()
          .post('folders', {
            title: this.newFolder.title,
            parentFolderUuid: this.newFolder.parentFolder.uuid,
          })
          .then((response) => {
            resolve(response)
          })
          .catch((errors) => {
            reject(errors)
          })
      })
    },
    saveToExistingFolder(folderUuid) {
      return new Promise((resolve, reject) => {
        this.$api
          .active()
          .put('documents/set-folders', {
            uuids: this.uploadedFileUuids,
            folderUuid: folderUuid,
          })
          .then((response) => {
            resolve(response)
          })
          .catch(() => {
            reject()
          })
      })
    },
    close() {
      this.currentPane = 'fileUpload'
      this.isUploading = false
      this.queuedCount = 0
      this.uploadingTo = 'existingFolder'
      this.uploadedFileUuids = []
      this.targetFolder = null
      this.newFolder = {
        title: '',
        parentFolder: {
          uuid: '',
        },
      }

      this.$store.commit('documentCloseUploadDocumentModal')

      this.$refs.documentUpload.removeAllFiles()
    },
    setDefaultFolders() {
      this.targetFolder = this.newFolder.parentFolder = _.find(this.existingFolders, {
        uuid: this.folderUuid,
      })
    },
    calculateClass(level) {
      const margin = {
        1: 'ml-2',
        2: 'ml-4',
        3: 'ml-6',
      }

      return level > 1 ? `text-grey-darker ${margin[level]}` : ''
    },
    setUploadDestination(destination) {
      this.currentPane = this.uploadingTo = destination
    },
    uploadHasCompleted() {
      this.isUploading = false
    },
    fileHasBeenRemoved(file) {
      this.queuedCount--

      const index = this.uploadedFileUuids.indexOf(file.uuid)

      if (index > -1) {
        this.uploadedFileUuids.splice(index, 1)
      }
    },
    fileHasBeenUploaded(file, uuid) {
      file.uuid = uuid

      this.uploadedFileUuids.push(uuid)
    },
    filesHaveBeenAdded() {
      this.queuedCount++
      this.isUploading = true
    },
  },
}
</script>
